import {
  DragEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  RiArrowRightSLine,
  RiDraggable,
  RiMoonLine,
  RiMoreLine,
  RiSearchLine,
  RiSettings3Line,
  RiSunLine,
} from '@remixicon/react';
import {
  AnalyticsContext,
  configApiRef,
  featureFlagsApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { getFlag } from '../../../helpers/helpers';
import { SearchModal, useSearchModal } from '@backstage/plugin-search';
import { SearchContextProvider } from '@backstage/plugin-search-react';
import { QuickSearchModal } from '../../search/QuickSearchModal';
import {
  SidebarDropTarget,
  useSidebarFavorites,
} from '../SidebarCustomization';
import {
  DEFAULT_SIDEBAR_FAVORITES,
  LEGACY_SIDEBAR_SECTIONS,
  SidebarNavItem,
  useSidebarNavItems,
} from './sidebarNavigation';
import { SidebarGroupFlyout } from './SidebarGroupFlyout';
import { SidebarMorePanel } from './SidebarMorePanel';
import { SidebarNotifications } from './SidebarNotifications';
import { useThemeToggle } from './useThemeToggle';
import { useSidebarAnalytics, useSidebarViewEvent } from './sidebarAnalytics';
import './appSidebar.css';

const MAKE_LOGO_URL = 'https://cdn.make.com/img/make/make_app_white_logo.png';
export const NOTIFICATIONS_FLAG = 'notifications';
export const SIDEBAR_SEARCH_HIDDEN_STORAGE_KEY =
  'backstage.sidebar.search.hidden.v1';
// Flyouts open at once. Moving to another sidebar item closes them at once
// too; only leaving the sidebar waits briefly, so the pointer can cross the
// gap to a flyout or slip out of it without losing it.
const FLYOUT_CLOSE_DELAY_MS = 150;
const SEARCH_PATH = '/search';
const DEFAULT_FAVORITES = [SEARCH_PATH, ...DEFAULT_SIDEBAR_FAVORITES];
const SEARCH_ITEM: SidebarNavItem = {
  path: SEARCH_PATH,
  title: 'Search',
  shortTitle: 'Search',
  icon: RiSearchLine,
  group: 'Platform',
};

const setSearchHidden = (hidden: boolean) => {
  try {
    window.localStorage.setItem(
      SIDEBAR_SEARCH_HIDDEN_STORAGE_KEY,
      JSON.stringify(hidden),
    );
  } catch {
    // Storage failures must not prevent sidebar customization.
  }
};

export const isSidebarPathActive = (path: string, pathname: string) => {
  if (path === '/') return pathname === '/';
  // AI Spend links to its reporting tab but owns the whole /ai-spend tree.
  const root = path === '/ai-spend/reporting' ? '/ai-spend' : path;
  return pathname === root || pathname.startsWith(`${root}/`);
};

const shortcutLabel = () =>
  typeof navigator !== 'undefined' &&
  /Macintosh|Mac OS X|iPhone|iPad|iPod/.test(navigator.userAgent)
    ? '⌘K'
    : 'Ctrl K';

type FlyoutState = {
  item: SidebarNavItem;
  anchor: HTMLElement;
  focus: boolean;
};

type AppSidebarProps = {
  narrow: boolean;
  onNarrowChange: (narrow: boolean) => void;
};

export const AppSidebar = ({ narrow, onNarrowChange }: AppSidebarProps) => {
  const items = useSidebarNavItems();
  const allItems = [SEARCH_ITEM, ...items];
  const [initialSearchFavorites] = useState(() => {
    try {
      return window.localStorage.getItem(SIDEBAR_SEARCH_HIDDEN_STORAGE_KEY) ===
        'true'
        ? []
        : [SEARCH_PATH];
    } catch {
      return [SEARCH_PATH];
    }
  });
  const location = useLocation();
  const mode = narrow ? 'narrow' : 'expanded';
  useSidebarViewEvent('v2', mode);
  const track = useSidebarAnalytics('v2', mode);
  const theme = useThemeToggle();
  const config = useApi(configApiRef);
  const featureFlags = useApi(featureFlagsApiRef);
  // The config flag says the notifications backend runs here; the user flag
  // is the opt-in while the bell is new.
  const showNotifications =
    getFlag(config, NOTIFICATIONS_FLAG) &&
    featureFlags.isActive(NOTIFICATIONS_FLAG);
  const { state: searchState, toggleModal } = useSearchModal();
  const idPrefix = useId();
  const navRef = useRef<HTMLElement>(null);
  const moreRef = useRef<HTMLButtonElement>(null);

  const pathKey = items.map(item => item.path).join('|');
  const sections = useMemo(() => {
    const mainSections = LEGACY_SIDEBAR_SECTIONS(pathKey.split('|'));
    mainSections[0] = [SEARCH_PATH, ...mainSections[0]];
    return [...mainSections, []];
  }, [pathKey]);
  const {
    favorites,
    getSection,
    moveFavorite,
    removeFavorite,
    resetFavorites,
  } = useSidebarFavorites(DEFAULT_FAVORITES, sections, {
    initialAdditions: initialSearchFavorites,
    fallbackSection: sections.length - 2,
  });

  const byPath = new Map(allItems.map(item => [item.path, item]));
  const favoriteItems = favorites.flatMap(path => {
    const item = byPath.get(path);
    return item ? [item] : [];
  });
  const favoritePaths = new Set(favoriteItems.map(item => item.path));
  const activeItem = items.find(item =>
    isSidebarPathActive(item.path, location.pathname),
  );

  // ---- More panel --------------------------------------------------------
  // Hovering More previews the panel and it closes when the pointer leaves;
  // clicking pins it open until it's closed. Using a previewed panel (a click,
  // focus or typing in it) holds it open the same way, without moving focus:
  // filtering shrinks the panel, which could otherwise leave the pointer
  // outside it and close the panel mid-word.
  const [more, setMore] = useState<'hover' | 'held' | 'pinned'>();
  const moreOpen = more !== undefined;
  const moreKept = more === 'held' || more === 'pinned';
  const moreTimer = useRef<number>();
  const cancelMoreClose = () => window.clearTimeout(moreTimer.current);
  const closeMore = useCallback((restoreFocus: boolean) => {
    window.clearTimeout(moreTimer.current);
    setMore(undefined);
    if (restoreFocus) moreRef.current?.focus();
  }, []);
  const scheduleMoreClose = () => {
    cancelMoreClose();
    moreTimer.current = window.setTimeout(
      () => setMore(current => (current === 'hover' ? undefined : current)),
      FLYOUT_CLOSE_DELAY_MS,
    );
  };
  const holdMore = () => {
    if (more !== 'hover') return;
    cancelMoreClose();
    setMore('held');
    track.capture('sidebar_more_pin', 'more', { source: 'panel' });
  };

  // ---- Group flyouts -----------------------------------------------------
  const [flyout, setFlyout] = useState<FlyoutState>();
  const flyoutRef = useRef<FlyoutState>();
  flyoutRef.current = flyout;
  const timer = useRef<number>();
  const clearTimer = () => window.clearTimeout(timer.current);
  const closeFlyout = useCallback((restoreFocus = false) => {
    window.clearTimeout(timer.current);
    if (restoreFocus) flyoutRef.current?.anchor.focus();
    setFlyout(undefined);
  }, []);
  const openOnHover = (item: SidebarNavItem, anchor: HTMLElement) => {
    clearTimer();
    // A pinned More panel already covers this space; a previewed one gives
    // way to the group under the pointer.
    if (moreKept) return;
    if (more === 'hover') closeMore(false);
    if (flyoutRef.current?.item.path === item.path) return;
    setFlyout({ item, anchor, focus: false });
  };
  const scheduleClose = () => {
    clearTimer();
    timer.current = window.setTimeout(
      () => setFlyout(undefined),
      FLYOUT_CLOSE_DELAY_MS,
    );
  };
  // The pointer reached another sidebar item: nothing to wait for.
  const closePopupsNow = () => {
    clearTimer();
    setFlyout(undefined);
    if (more === 'hover') closeMore(false);
  };

  // Popups must never outlive the page or the window they were opened in;
  // stale timers are what made the legacy flyouts blink after a tab switch.
  useEffect(() => {
    closeFlyout();
    closeMore(false);
  }, [location.pathname, location.search, closeFlyout, closeMore]);
  useEffect(() => {
    const close = () => {
      if (document.visibilityState === 'hidden' || !document.hasFocus()) {
        closeFlyout();
      }
    };
    window.addEventListener('blur', close);
    document.addEventListener('visibilitychange', close);
    return () => {
      window.removeEventListener('blur', close);
      document.removeEventListener('visibilitychange', close);
      window.clearTimeout(timer.current);
    };
  }, [closeFlyout]);

  // ---- Favourites --------------------------------------------------------
  const bottomSection = sections.length - 1;
  const lastSection = bottomSection - 1;
  const reorder = (
    path: string,
    target: SidebarDropTarget,
    source: 'drag' | 'keyboard',
  ) => {
    moveFavorite(path, target);
    track.capture('sidebar_reorder', path, { source });
    if (source === 'keyboard') {
      requestAnimationFrame(() => {
        const row = Array.from(
          navRef.current?.querySelectorAll<HTMLElement>(
            '[data-sidebar-path]',
          ) ?? [],
        ).find(element => element.dataset.sidebarPath === path);
        row?.focus();
      });
    }
  };
  const toggleFavorite = (item: SidebarNavItem) => {
    if (item.path === SEARCH_PATH)
      setSearchHidden(favoritePaths.has(item.path));
    if (favoritePaths.has(item.path)) {
      removeFavorite(item.path);
      track.capture('sidebar_unpin', item.path, { source: 'more' });
    } else {
      moveFavorite(item.path, { section: lastSection, before: null });
      track.capture('sidebar_pin', item.path, { source: 'more' });
    }
  };
  const reset = () => {
    setSearchHidden(false);
    resetFavorites();
    track.capture('sidebar_reset', 'favourites', { source: 'more' });
  };

  const [dragPath, setDragPath] = useState<string>();
  const [dropTarget, setDropTarget] = useState<SidebarDropTarget>();
  const endDrag = () => {
    setDragPath(undefined);
    setDropTarget(undefined);
  };
  const onDrop = (event: DragEvent, target: SidebarDropTarget) => {
    if (!dragPath) return;
    event.preventDefault();
    event.stopPropagation();
    reorder(dragPath, target, 'drag');
    endDrag();
  };
  const moveRegion = (item: SidebarNavItem, bottom: boolean) => {
    reorder(
      item.path,
      {
        section: bottom ? bottomSection : lastSection,
        before: bottom
          ? (favoriteItems.find(
              favorite => getSection(favorite.path) === bottomSection,
            )?.path ?? null)
          : null,
      },
      'keyboard',
    );
  };

  // Alt+ArrowUp/Down reorders favourites from the keyboard.
  const onFavoriteKeyDown = (
    event: KeyboardEvent<HTMLElement>,
    item: SidebarNavItem,
    index: number,
    regionItems: SidebarNavItem[],
    section: number,
  ) => {
    if (event.key === 'ArrowRight' && item.submenu) {
      event.preventDefault();
      clearTimer();
      setFlyout({ item, anchor: event.currentTarget, focus: true });
      return;
    }
    if (!event.altKey || !['ArrowUp', 'ArrowDown'].includes(event.key)) {
      return;
    }
    event.preventDefault();
    const paths = regionItems.map(favorite => favorite.path);
    if (event.key === 'ArrowUp' && index > 0) {
      const before = paths[index - 1];
      reorder(item.path, { before, section: getSection(before) }, 'keyboard');
    } else if (event.key === 'ArrowUp' && section === bottomSection) {
      moveRegion(item, false);
    } else if (event.key === 'ArrowDown' && index < paths.length - 1) {
      const before = paths[index + 2] ?? null;
      reorder(
        item.path,
        { before, section: before ? getSection(before) : section },
        'keyboard',
      );
    } else if (event.key === 'ArrowDown' && section !== bottomSection) {
      moveRegion(item, true);
    }
  };

  const renderFavorites = (bottom: boolean) => {
    const section = bottom ? bottomSection : lastSection;
    const regionItems = favoriteItems.filter(
      item => (getSection(item.path) === bottomSection) === bottom,
    );
    const appendTarget = { section, before: null };
    return (
      // Native drop targets retain list semantics; Alt+Arrow keys provide keyboard access.
      // oxlint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
      <ul
        className={[
          'make-sidebar__list',
          bottom && 'make-sidebar__bottom',
          dragPath && 'make-sidebar__list--drag-active',
          dragPath &&
            dropTarget?.section === section &&
            dropTarget.before === null &&
            'make-sidebar__list--drop-end',
        ]
          .filter(Boolean)
          .join(' ')}
        aria-label={bottom ? 'Bottom favourites' : 'Favourites'}
        onDragOver={event => {
          if (!dragPath) return;
          event.preventDefault();
          event.dataTransfer.dropEffect = 'move';
          setDropTarget(appendTarget);
        }}
        onDragLeave={event => {
          if (
            !event.currentTarget.contains(event.relatedTarget as Node | null)
          ) {
            setDropTarget(undefined);
          }
        }}
        onDrop={event => onDrop(event, appendTarget)}
      >
        {regionItems.map((item, index) => {
          const Icon = item.icon;
          const search = item.path === SEARCH_PATH;
          const active = !search && activeItem?.path === item.path;
          const flyoutId = `${idPrefix}-flyout-${favoriteItems.indexOf(item)}`;
          const targetAt = (event: DragEvent<HTMLElement>) => {
            const { top, height } = event.currentTarget.getBoundingClientRect();
            const before =
              event.clientY > top + height / 2
                ? (regionItems[index + 1]?.path ?? null)
                : item.path;
            return { section: before ? getSection(before) : section, before };
          };
          const dragProps = {
            draggable: true,
            'data-sidebar-path': item.path,
            'aria-label': item.title,
            'aria-keyshortcuts': search
              ? 'Meta+K Control+K Alt+ArrowUp Alt+ArrowDown'
              : 'Alt+ArrowUp Alt+ArrowDown',
            onDragStart: (event: DragEvent<HTMLElement>) => {
              closeFlyout();
              closeMore(false);
              setDragPath(item.path);
              event.dataTransfer.effectAllowed = 'move';
              event.dataTransfer.setData('text/plain', item.path);
            },
            onDragOver: (event: DragEvent<HTMLElement>) => {
              if (!dragPath) return;
              event.preventDefault();
              event.stopPropagation();
              event.dataTransfer.dropEffect = 'move';
              setDropTarget(targetAt(event));
            },
            onDrop: (event: DragEvent<HTMLElement>) =>
              onDrop(event, targetAt(event)),
            onDragEnd: endDrag,
            onKeyDown: (event: KeyboardEvent<HTMLElement>) =>
              onFavoriteKeyDown(event, item, index, regionItems, section),
          };
          const content = (
            <>
              <Icon size={20} aria-hidden />
              <span className="make-sidebar__label">{item.title}</span>
              <span className="make-sidebar__short" aria-hidden="true">
                {item.shortTitle}
              </span>
              {search ? (
                <kbd className="make-sidebar__kbd" aria-hidden="true">
                  {shortcutLabel()}
                </kbd>
              ) : item.submenu ? (
                <RiArrowRightSLine
                  className="make-sidebar__chevron"
                  aria-hidden
                />
              ) : (
                <RiDraggable className="make-sidebar__grip" aria-hidden />
              )}
            </>
          );
          return (
            <li
              key={item.path}
              className={[
                'make-sidebar__item',
                dragPath === item.path && 'make-sidebar__item--dragging',
                dragPath &&
                  dropTarget?.before === item.path &&
                  'make-sidebar__item--drop',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {search ? (
                <button
                  {...dragProps}
                  type="button"
                  className="make-sidebar__row make-sidebar__search"
                  onPointerEnter={closePopupsNow}
                  onClick={toggleModal}
                >
                  {content}
                </button>
              ) : (
                <Link
                  {...dragProps}
                  to={item.path}
                  className="make-sidebar__row"
                  aria-current={active ? 'page' : undefined}
                  aria-haspopup={item.submenu ? 'true' : undefined}
                  aria-expanded={
                    item.submenu ? flyout?.item.path === item.path : undefined
                  }
                  aria-controls={
                    flyout?.item.path === item.path ? flyoutId : undefined
                  }
                  title={
                    narrow && item.shortTitle !== item.title
                      ? item.title
                      : undefined
                  }
                  onPointerEnter={event =>
                    item.submenu
                      ? openOnHover(item, event.currentTarget)
                      : closePopupsNow()
                  }
                  onPointerLeave={() => item.submenu && scheduleClose()}
                  onClick={() => track.navigate(item.path, 'favourites')}
                >
                  {content}
                </Link>
              )}
            </li>
          );
        })}
        {dragPath && regionItems.length === 0 && (
          <li className="make-sidebar__drop-hint">
            {bottom ? 'Drop here to pin to bottom' : 'Drop here to pin to top'}
          </li>
        )}
      </ul>
    );
  };

  const flyoutLeft = () =>
    (navRef.current?.getBoundingClientRect().right ?? 0) + 4;

  return (
    <nav
      ref={navRef}
      className={`make-sidebar${narrow ? ' make-sidebar--narrow' : ''}`}
      aria-label="Main"
    >
      {/* Like the legacy sidebar, the logo expands and narrows the sidebar. */}
      <button
        type="button"
        className="make-sidebar__brand"
        aria-label={narrow ? 'Expand sidebar' : 'Narrow sidebar'}
        aria-expanded={!narrow}
        aria-keyshortcuts="["
        title={narrow ? 'Expand sidebar' : 'Narrow sidebar'}
        onPointerEnter={closePopupsNow}
        onClick={() => onNarrowChange(!narrow)}
      >
        <span className="make-sidebar__logo" aria-hidden="true">
          <img src={MAKE_LOGO_URL} alt="" />
        </span>
        <span className="make-sidebar__brand-name" aria-hidden="true">
          Backstage
        </span>
      </button>

      <div className="make-sidebar__scroll">
        {renderFavorites(false)}
        <hr className="make-sidebar__divider" />

        <button
          ref={moreRef}
          type="button"
          aria-label="More"
          className="make-sidebar__row"
          aria-haspopup="dialog"
          aria-expanded={moreOpen}
          aria-controls={moreOpen ? `${idPrefix}-more` : undefined}
          aria-current={
            activeItem && !favoritePaths.has(activeItem.path)
              ? 'page'
              : undefined
          }
          data-open={moreOpen || undefined}
          onPointerEnter={() => {
            closeFlyout();
            cancelMoreClose();
            if (!moreOpen) {
              setMore('hover');
              track.capture('sidebar_more_open', 'more', { source: 'hover' });
            }
          }}
          onPointerLeave={scheduleMoreClose}
          onClick={() => {
            closeFlyout();
            cancelMoreClose();
            if (moreKept) {
              closeMore(false);
              return;
            }
            track.capture(
              more === 'hover' ? 'sidebar_more_pin' : 'sidebar_more_open',
              'more',
              { source: 'click' },
            );
            setMore('pinned');
          }}
        >
          <RiMoreLine size={20} aria-hidden />
          <span className="make-sidebar__label">More</span>
          <span className="make-sidebar__short" aria-hidden="true">
            More
          </span>
          <RiArrowRightSLine className="make-sidebar__chevron" aria-hidden />
        </button>
      </div>

      {renderFavorites(true)}

      <div className="make-sidebar__footer">
        <Link
          to="/settings"
          aria-label="Settings"
          className="make-sidebar__row make-sidebar__settings"
          aria-current={
            location.pathname.startsWith('/settings') ? 'page' : undefined
          }
          onPointerEnter={closePopupsNow}
          onClick={() => track.navigate('/settings', 'footer')}
        >
          <RiSettings3Line size={20} aria-hidden />
          <span className="make-sidebar__label">Settings</span>
          <span className="make-sidebar__short" aria-hidden="true">
            Settings
          </span>
        </Link>
        {showNotifications && (
          <SidebarNotifications
            id={`${idPrefix}-notifications`}
            panelLeft={() => flyoutLeft() + 4}
            onPointerEnter={closePopupsNow}
            onOpen={() => {
              closePopupsNow();
              track.capture('notifications_open', 'sidebar', {
                source: 'sidebar',
              });
            }}
            onOpenNotification={notification =>
              track.capture(
                'notifications_click',
                notification.payload.topic ?? 'notification',
                {
                  source: notification.origin,
                  category: notification.payload.severity ?? 'normal',
                  status: notification.read ? 'read' : 'unread',
                },
              )
            }
          />
        )}
        <button
          type="button"
          className="make-sidebar__row make-sidebar__theme"
          aria-label={theme.isDark ? 'Use light theme' : 'Use dark theme'}
          title={theme.isDark ? 'Use light theme' : 'Use dark theme'}
          onPointerEnter={closePopupsNow}
          onClick={() => {
            track.capture('theme_change', theme.isDark ? 'light' : 'dark', {
              source: 'sidebar',
            });
            theme.toggle();
          }}
        >
          {theme.isDark ? (
            <RiSunLine size={20} aria-hidden />
          ) : (
            <RiMoonLine size={20} aria-hidden />
          )}
          <span className="make-sidebar__short" aria-hidden="true">
            {theme.isDark ? 'Light' : 'Dark'}
          </span>
        </button>
      </div>

      {flyout && (
        <SidebarGroupFlyout
          id={`${idPrefix}-flyout-${favoriteItems.findIndex(
            item => item.path === flyout.item.path,
          )}`}
          item={flyout.item}
          anchor={flyout.anchor}
          left={flyoutLeft()}
          focusFirst={flyout.focus}
          onPointerEnter={clearTimer}
          onPointerLeave={scheduleClose}
          onClose={closeFlyout}
          onNavigate={to => track.navigate(to, 'flyout')}
        />
      )}
      {moreOpen && moreRef.current && (
        <SidebarMorePanel
          id={`${idPrefix}-more`}
          items={allItems}
          favorites={favoritePaths}
          activePath={activeItem?.path}
          anchor={moreRef.current}
          left={flyoutLeft() + 4}
          onToggleFavorite={toggleFavorite}
          onReset={reset}
          onSearch={() => {
            closeMore(true);
            toggleModal();
          }}
          onNavigate={to => track.navigate(to, 'more')}
          onClose={closeMore}
          pinned={more === 'pinned'}
          onEngage={holdMore}
          onPointerEnter={cancelMoreClose}
          onPointerLeave={scheduleMoreClose}
        />
      )}
      {/* Same search wiring as the legacy sidebar in Root.tsx: the palette
      context tags only search events, not the sidebar's own. */}
      <AnalyticsContext attributes={{ searchSurface: 'palette' }}>
        <SearchContextProvider
          initialState={{ pageLimit: 25, term: '', types: [], filters: {} }}
        >
          <SearchModal {...searchState} toggleModal={toggleModal}>
            {({ toggleModal: closeModal }) => (
              <QuickSearchModal toggleModal={closeModal} />
            )}
          </SearchModal>
        </SearchContextProvider>
      </AnalyticsContext>
    </nav>
  );
};
